import { FC } from 'react';

import { Form, FormikProvider, useFormik } from 'formik';

import {
  PRE_CHAT_INITIAL_VALUES,
  PreChatSchema,
} from 'modules/webchat/presentation/validations';
import {
  CheckInput,
  Modal,
  Spinner,
} from 'shared/presentation/components/atoms';
import { Button, TextInput } from 'shared/presentation/components/molecules';
import { ICONS, MASKS } from 'shared/presentation/constants';
import { Link, Trans, useLocale, useTheme } from 'shared/presentation/contexts';
import { useMediaQuery } from 'shared/presentation/hooks';

import { usePreChat } from './hooks';
import * as S from './styles';

const CloseIcon = ICONS.X;

interface IPreChatModalProps {
  /** Fecha e desmonta o modal: pelo X, pelo overlay ou ao terminar o fluxo. */
  onClose(): void;
}

/**
 * Pré-chat do Webchat (decisão 1 do ED-7076): nome, celular e consentimento
 * antes de montar o widget da Callbell. O chat abre logo depois; quem
 * identifica a pessoa e cria o lead é o bot da Callbell, pelo celular.
 */
const PreChatModal: FC<IPreChatModalProps> = ({ onClose }) => {
  const { t, language } = useLocale('webchat');
  const { theme } = useTheme();
  const isMobile = useMediaQuery('md');

  const controller = usePreChat({ onFinish: onClose });

  const formik = useFormik({
    initialValues: PRE_CHAT_INITIAL_VALUES,
    validationSchema: PreChatSchema({ t, language }),
    onSubmit: values => controller.submit(values),
  });

  const renderContent = () => {
    if (controller.step === 'openingChat') {
      return (
        <S.Feedback role="status">
          <Spinner color={theme.palette.primary.main} />
          <p>{t('Opening the chat...')}</p>
        </S.Feedback>
      );
    }

    return (
      <FormikProvider value={formik}>
        <Form>
          <TextInput
            name="name"
            label={t('Full name')}
            placeholder={t('Full name')}
            icon={ICONS.USER}
          />

          <TextInput
            name="phone"
            type="tel"
            inputMode="tel"
            label={t('Cellphone')}
            placeholder={t('PHONE_PLACEHOLDER')}
            mask={MASKS.PHONE[language]}
            icon={ICONS.PHONE}
          />

          <CheckInput name="terms">
            <Trans
              t={t}
              localeKey="I accept the <1>Terms and conditions</1> and I authorize the use of my data according to the <3>Privacy policy</3>"
            >
              {'I accept the '}
              <Link to="/termos-de-uso" target="_blank">
                Terms and conditions
              </Link>
              {' and I authorize the use of my data according to the '}
              <Link to="/politicas-de-privacidade" target="_blank">
                Privacy policy
              </Link>
            </Trans>
          </CheckInput>

          <Button
            variant="primary"
            type="submit"
            icon={ICONS.MESSAGE}
            iconPosition="right"
            disabled={!formik.isValid || !formik.dirty}
          >
            {t('Start conversation')}
          </Button>
        </Form>
      </FormikProvider>
    );
  };

  return (
    <Modal initialOpen align={isMobile ? 'bottom' : 'center'} onClose={onClose}>
      <S.Container>
        <header>
          <div>
            {controller.isFormVisible && (
              <h2>
                {t('Tell us your name and cellphone to start the conversation')}
              </h2>
            )}
          </div>

          <button type="button" aria-label={t('Close')} onClick={onClose}>
            <CloseIcon />
          </button>
        </header>

        {renderContent()}
      </S.Container>
    </Modal>
  );
};

export default PreChatModal;
